import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { Icon } from './Icon';
import PostImages from './PostImages';
import ProtocolShield from './ProtocolShield';
import InlineProtocolReport from './InlineProtocolReport';
import { useProtocolActions } from '../hooks/useProtocolActions';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';
import { 
  getExtendedRange, 
  getUserValuePosition, 
  getClinicalRangePosition,
  getClinicalRanges,
  extractWebsiteName,
  formatBiomarkerValueForDisplay
} from '../utils/biomarkerUtils';
import './AttachmentDisplay.css';

/**
 * AttachmentDisplay - Unified component for displaying attachments
 * Used in both PostCreationBox (with remove buttons) and PostCard (display only)
 * 
 * Props:
 * - attachments: Object with protocol, biomarker, images[], protocolReport
 * - onRemove: Optional function for removing attachments (PostCreationBox only)
 * - onBiohackClick: Optional function for biohack click handler
 * - mode: 'preview' (PostCreationBox) or 'display' (PostCard)
 */

// Protocol Report Component
const ProtocolReportDisplay = ({ protocolReport, isHovered, onRemove, mode }) => {
  const [pageInfo, setPageInfo] = useState({ currentPage: 1, totalPages: 1 });
  const [isHoveringReport, setIsHoveringReport] = useState(false);

  const handlePageChange = useCallback((currentPage, totalPages) => {
    setPageInfo(prev => {
      if (prev.currentPage === currentPage && prev.totalPages === totalPages) {
        return prev;
      }
      return { currentPage, totalPages };
    });
  }, []);

  const showBars = isHovered || isHoveringReport;

  return (
    <div
      className={`attachment-display-protocol-report ${showBars ? 'is-hovered' : ''} ${mode === 'preview' ? 'preview-mode' : ''}`}
      onMouseEnter={() => setIsHoveringReport(true)}
      onMouseLeave={() => setIsHoveringReport(false)}
    >
      {mode === 'display' && (
        <>
          <div className="attachment-display-protocol-report-hover-bar attachment-display-protocol-report-hover-bar--top">
            <span className="attachment-display-protocol-report-page-numbers">
              {pageInfo.currentPage} / {pageInfo.totalPages}
            </span>
          </div>
          {/* Bottom bar rendered before content so portal target exists when ProtocolReport mounts */}
          <div className="attachment-display-protocol-report-hover-bar attachment-display-protocol-report-hover-bar--bottom" id={`protocol-report-controls-${protocolReport.id}`} />
          <div className="attachment-display-protocol-report-content">
            <InlineProtocolReport
              snapshotId={protocolReport.id}
              protocolId={protocolReport.protocol_id}
              onPageChange={handlePageChange}
              controlsPortalId={`protocol-report-controls-${protocolReport.id}`}
            />
          </div>
        </>
      )}
      {mode === 'preview' && (
        <>
          <div className="attachment-display-protocol-report-hover-bar attachment-display-protocol-report-hover-bar--top">
            <span className="attachment-display-protocol-report-page-numbers">
              {pageInfo.currentPage} / {pageInfo.totalPages}
            </span>
          </div>
          {/* Bottom bar rendered before content so portal target exists when ProtocolReport mounts */}
          <div className="attachment-display-protocol-report-hover-bar attachment-display-protocol-report-hover-bar--bottom" id={`protocol-report-controls-${protocolReport.id}`} />
          <div className="attachment-display-protocol-report-content">
            <InlineProtocolReport
              snapshotId={protocolReport.id}
              protocolId={protocolReport.protocol_id}
              onPageChange={handlePageChange}
              controlsPortalId={`protocol-report-controls-${protocolReport.id}`}
            />
          </div>
        </>
      )}
      {onRemove && (
        <button
          className="attachment-display-remove attachment-display-remove-report"
          onClick={onRemove}
          type="button"
          aria-label="Remove report"
        >
          ×
        </button>
      )}
    </div>
  );
};

// Protocol Display
const ProtocolDisplay = ({ protocol, onRemove, onBiohackClick, userVotes, handleVoteAction, onAddProtocolToStaged, mode }) => {
  const handleLike = async (protocol) => {
    await handleVoteAction(
      protocol,
      1,
      () => {},
      (errorMsg) => console.error('Error liking protocol:', errorMsg)
    );
  };

  const handleDislike = async (protocol) => {
    await handleVoteAction(
      protocol,
      -1,
      () => {},
      (errorMsg) => console.error('Error disliking protocol:', errorMsg)
    );
  };

  // Hide FAB menu and inspect button when in preview mode (PostCreationBox)
  const hideFabMenu = mode === 'preview';
  const hideInspectButton = mode === 'preview';

  return (
    <div className="attachment-display-protocol">
      <ProtocolShield
        protocol={protocol}
        size="medium"
        onBiohackClick={onBiohackClick}
        onLike={handleLike}
        onDislike={handleDislike}
        userVote={userVotes[protocol.id] || null}
        onAddToStaged={onAddProtocolToStaged}
        hideFabMenu={hideFabMenu}
        hideInspectButton={hideInspectButton}
      />
      {onRemove && (
        <button
          className="attachment-display-remove"
          onClick={onRemove}
          type="button"
          aria-label="Remove protocol"
        >
          ×
        </button>
      )}
    </div>
  );
};

// Biomarker Display - Copied design from user page biomarker cards (not linked)
const BiomarkerDisplay = ({ biomarker, onRemove }) => {
  const navigate = useNavigate();
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userGender, setUserGender] = useState(null);

  useEffect(() => {
    const getCurrentUser = async () => {
      // Use getSession() to avoid network requests
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (user) {
        setCurrentUserId(user.id);
        
        // Fetch user profile to get gender - try to reuse cached profile data first
        // Check if we already have profile data cached from Home/TopNavigation
        const profileCacheKey = getCacheKey('fetchUserProfile', user.id);
        let profile = null;
        
        // First, try to get from sessionStorage cache (set by Home.js/TopNavigation.js)
        const cachedProfile = sessionStorage.getItem(`userProfile_${user.id}`);
        if (cachedProfile) {
          try {
            const parsed = JSON.parse(cachedProfile);
            if (parsed.gender !== undefined) {
              profile = { gender: parsed.gender };
            }
          } catch (e) {
            // Invalid cache, continue to fetch
          }
        }
        
        // If not in cache, fetch using the same cache key as Home/TopNavigation
        // This will reuse the in-flight request if Home/TopNavigation already started it
        if (!profile) {
          profile = await dedupeRequest(profileCacheKey, async () => {
            const { data } = await supabase
              .from('user_profiles')
              .select('username, profile_picture_url, gender')
              .eq('user_id', user.id)
              .maybeSingle();
            return data;
          }, { caller: 'AttachmentDisplay' });
        }
        
        if (profile) {
          setUserGender(profile.gender);
        }
      }
    };
    getCurrentUser();
  }, []);

  const result = biomarker.result;
  const hasResult = !!result;
  const displayValue = hasResult ? formatBiomarkerValueForDisplay(result.value) : '';
  const displayUnit = hasResult ? (result.unit_override || biomarker.unit) : biomarker.unit || '';
  const testDate = hasResult && result.panel?.test_date ? new Date(result.panel.test_date).toISOString().split('T')[0] : '';

  // When value has 3+ digits, scale down for biomarker attachments (result -10%, range -30%)
  const digitCount = String(displayValue).replace(/\D/g, '').length;
  const hasThreeDigitValue = digitCount >= 3;

  // Get gender-specific clinical ranges
  const ranges = getClinicalRanges(biomarker, userGender);
  const enrichedBiomarker = { ...biomarker, ...ranges };

  const userValuePos = getUserValuePosition(enrichedBiomarker, result, userGender);
  const clinicalRangePos = getClinicalRangePosition(enrichedBiomarker, userGender);

  const handleBiomarkerNavigate = (event) => {
    event.stopPropagation();
    if (currentUserId && biomarker.id) {
      navigate(`/user/${currentUserId}#biomarkers?highlight=${biomarker.id}`);
    }
  };

  const handleBiomarkerKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleBiomarkerNavigate(event);
    }
  };

  const handleCopySource = async (e) => {
    e.stopPropagation(); // Prevent tooltip from closing
    if (biomarker.description_source) {
      try {
        await navigator.clipboard.writeText(biomarker.description_source);
        // Optional: Show a brief feedback message
        const originalText = e.target.textContent;
        e.target.textContent = 'Copied!';
        setTimeout(() => {
          e.target.textContent = originalText;
        }, 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const handleCopyRangeSource = async (e) => {
    e.stopPropagation();
    if (biomarker.range_source) {
      try {
        await navigator.clipboard.writeText(biomarker.range_source);
        const button = e.target.closest('.attachment-display-biomarker-value-range-wrapper').querySelector('button');
        if (button) {
          const originalText = button.textContent;
          button.textContent = 'Copied!';
          setTimeout(() => {
            button.textContent = originalText;
          }, 2000);
        }
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <div className={`attachment-display-biomarker ${!hasResult ? 'no-data' : ''} ${hasThreeDigitValue ? 'attachment-display-biomarker--value-three-digits' : ''}`}>
      <div className="attachment-display-biomarker-header">
        <div className="attachment-display-biomarker-title">
          <Icon name="TestTube" className="attachment-display-biomarker-icon" />
          <h3
            className={currentUserId && !onRemove ? 'attachment-display-biomarker-name-clickable' : ''}
            onClick={currentUserId && !onRemove ? handleBiomarkerNavigate : undefined}
            onKeyDown={currentUserId && !onRemove ? handleBiomarkerKeyDown : undefined}
            role={currentUserId && !onRemove ? 'button' : undefined}
            tabIndex={currentUserId && !onRemove ? 0 : undefined}
          >
            {biomarker.name}
          </h3>
        </div>
        {biomarker.description && (
          <div className="attachment-display-biomarker-info-wrapper">
            <div className="attachment-display-biomarker-info" aria-label="Biomarker information" role="button" tabIndex={0}>
              <Icon name="Info" />
              <div className="attachment-display-biomarker-tooltip">
                {biomarker.description}
                {biomarker.description_source && (
                  <p>
                    Source:{' '}
                    <button
                      type="button"
                      onClick={handleCopySource}
                    >
                      {extractWebsiteName(biomarker.description_source)}
                    </button>
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="attachment-display-biomarker-content">
        <div className="attachment-display-biomarker-value">
          {!hasResult && biomarker.resultDeleted && (
            <span className="attachment-display-biomarker-unavailable">Result no longer available</span>
          )}
          {hasResult && (
            <>
              <span className="attachment-display-biomarker-value-number">{displayValue}</span>
              <span className="attachment-display-biomarker-value-unit">{displayUnit}</span>
              {(ranges.clinical_low !== null || ranges.clinical_high !== null) && (
                <div className="attachment-display-biomarker-value-range-wrapper">
                  <span className="attachment-display-biomarker-value-range">
                    Range: {ranges.clinical_low !== null ? formatBiomarkerValueForDisplay(ranges.clinical_low) : '•'}-{ranges.clinical_high !== null ? formatBiomarkerValueForDisplay(ranges.clinical_high) : '•'}
                  </span>
                  {biomarker.range_source && (
                    <div className="attachment-display-biomarker-value-range-tooltip">
                      <p>
                        Source:{' '}
                        <button
                          type="button"
                          onClick={handleCopyRangeSource}
                        >
                          {extractWebsiteName(biomarker.range_source)}
                        </button>
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="attachment-display-biomarker-progress">
          <div className="attachment-display-biomarker-progress-bar">
            {/* Clinical range background (supports partial ranges, e.g. high-only like eGFR) */}
            {(ranges.clinical_low !== null || ranges.clinical_high !== null) && (
              <div 
                className="attachment-display-biomarker-progress-clinical-range"
                style={{
                  left: `${clinicalRangePos.start}%`,
                  width: `${clinicalRangePos.end - clinicalRangePos.start}%`
                }}
              />
            )}
            {/* User value vertical line */}
            {hasResult && userValuePos !== null && (
              <div 
                className="attachment-display-biomarker-progress-value-line"
                style={{
                  left: `${userValuePos}%`
                }}
              />
            )}
          </div>
        </div>

        <div className="attachment-display-biomarker-divider"></div>

        <div className="attachment-display-biomarker-tags">
          {biomarker.category && (
            <div className="attachment-display-biomarker-category-tag">
              {biomarker.category}
            </div>
          )}
          {testDate && (
            <div className="attachment-display-biomarker-test-tag">
              {testDate}
            </div>
          )}
        </div>
      </div>
      {onRemove && (
        <button
          className="attachment-display-remove"
          onClick={onRemove}
          type="button"
          aria-label="Remove biomarker"
        >
          ×
        </button>
      )}
    </div>
  );
};

// Image Display
const ImageDisplay = ({ image, onRemove }) => {
  // PostImages expects an array, so wrap single image in array
  const images = Array.isArray(image) ? image : [image];
  
  return (
    <div className="attachment-display-image">
      <PostImages images={images} isModule={true} />
      {onRemove && (
        <button
          className="attachment-display-remove"
          onClick={onRemove}
          type="button"
          aria-label="Remove image"
        >
          ×
        </button>
      )}
    </div>
  );
};

// Biohacks Display
const BiohacksDisplay = ({ biohacks, onBiohackClick, onRemove }) => {
  return (
    <div className="attachment-display-biohacks">
      <div className="attachment-display-label">Biohack</div>
      <div className="attachment-display-biohacks-content">
        <div className="attachment-display-biohacks-grid">
          {biohacks.slice(0, 6).map((biohack, index) => (
            <div
              key={biohack.id}
              className="attachment-display-biohack-item"
              style={{
                '--animation-delay': `${index * 0.1}s`,
                '--float-offset': `${index * 0.3}s`
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (onBiohackClick) {
                  onBiohackClick(biohack);
                }
              }}
            >
              <div className="attachment-display-biohack-image-wrapper">
                {biohack.image_url && (
                  <img
                    src={biohack.image_url}
                    alt={biohack.name}
                    className="attachment-display-biohack-image"
                    onError={(e) => {
                      e.target.src = '/default-biohack.jpg';
                    }}
                  />
                )}
              </div>
              <div className="attachment-display-biohack-name">
                {biohack.name}
              </div>
            </div>
          ))}
        </div>

        {biohacks.length > 6 && (
          <div className="attachment-display-biohacks-more">
            <Icon name="Plus" className="attachment-display-biohacks-more-icon" />
            <span className="attachment-display-biohacks-more-number">{biohacks.length - 6}</span>
            <span className="attachment-display-biohacks-more-text">more</span>
          </div>
        )}
      </div>
      {onRemove && (
        <button
          className="attachment-display-remove"
          onClick={onRemove}
          type="button"
          aria-label="Remove biohacks"
        >
          ×
        </button>
      )}
    </div>
  );
};

// Main Component
const AttachmentDisplay = ({
  attachments,
  modules,
  onRemove,
  onBiohackClick,
  mode = 'display', // 'preview' or 'display'
  userVotes = {}, // Passed from parent to avoid duplicate requests
  handleVote = null, // Passed from parent to avoid duplicate requests
  onAddProtocolToStaged = null // Handler to add protocol to staged protocols
}) => {
  const [isHovered, setIsHovered] = useState(false);
  // Always call hook (React rules), but only use it if props not provided (preview mode)
  // This prevents duplicate requests when AttachmentDisplay is used in PostFeed (display mode)
  const protocolActions = useProtocolActions();
  // Check if props are provided - userVotes could be {} (empty) or undefined, handleVote could be null
  const hasProps = userVotes !== undefined && handleVote !== null && handleVote !== undefined;
  const effectiveUserVotes = hasProps ? (userVotes || {}) : protocolActions.userVotes;
  const effectiveHandleVote = hasProps ? handleVote : protocolActions.handleVote;

  // Only fetch if props not provided (preview mode in PostCreationBox)
  // Use fetchUserVotes function directly (memoized with useCallback) instead of protocolActions object
  // to prevent duplicate calls when the object reference changes
  const { fetchUserVotes } = protocolActions;
  useEffect(() => {
    if (!hasProps) {
      fetchUserVotes();
    }
  }, [hasProps, fetchUserVotes]);

  // Handle both formats: attachments object (preview mode) or modules array (display mode)
  const renderContent = () => {
    // Preview mode: use attachments object
    if (mode === 'preview' && attachments) {
      // If we have a report, show only the report
      if (attachments.protocolReport) {
        return (
          <div className="attachment-display-container preview-mode">
            <div className="attachment-display-full-width">
              <ProtocolReportDisplay
                protocolReport={attachments.protocolReport}
                isHovered={isHovered}
                onRemove={onRemove ? () => onRemove('protocolReport', attachments.protocolReport.id) : null}
                mode={mode}
              />
            </div>
          </div>
        );
      }

      // Otherwise show other attachments in a line
      const items = [];

      if (attachments.protocol) {
        items.push({
          type: 'protocol',
          id: attachments.protocol.id,
          data: attachments.protocol
        });
      }

      if (attachments.biomarker) {
        items.push({
          type: 'biomarker',
          id: attachments.biomarker.id,
          data: attachments.biomarker
        });
      }

      attachments.images.forEach(img => {
        items.push({
          type: 'image',
          id: img.id,
          data: img
        });
      });

      if (items.length === 0) return null;

      return (
        <div className="attachment-display-container preview-mode">
          <div className="attachment-display-line">
            {items.map((item) => (
              <div key={item.id} className="attachment-display-item">
                {item.type === 'protocol' && (
                  <ProtocolDisplay
                    protocol={item.data}
                    onRemove={onRemove ? () => onRemove('protocol', item.id) : null}
                    onBiohackClick={onBiohackClick}
                    userVotes={effectiveUserVotes}
                    handleVoteAction={effectiveHandleVote}
                    onAddProtocolToStaged={onAddProtocolToStaged}
                    mode={mode}
                  />
                )}
                {item.type === 'biomarker' && (
                  <BiomarkerDisplay
                    biomarker={item.data}
                    onRemove={onRemove ? () => onRemove('biomarker', item.id) : null}
                  />
                )}
                {item.type === 'image' && (
                  <ImageDisplay
                    image={item.data}
                    onRemove={onRemove ? () => onRemove('image', item.id) : null}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Display mode: use modules array
    if (mode === 'display' && modules) {
      if (modules.length === 0) return null;

      // Check if we have a protocol report (takes full width)
      const reportModule = modules.find(m => m.type === 'protocolReport');
      if (reportModule) {
        return (
          <div className="attachment-display-container display-mode">
            <div className="attachment-display-full-width">
              <ProtocolReportDisplay
                protocolReport={reportModule.data}
                isHovered={isHovered}
                onRemove={null}
                mode={mode}
              />
            </div>
          </div>
        );
      }

      // Otherwise show all modules in a line
      return (
        <div className="attachment-display-container display-mode">
          <div className="attachment-display-line">
            {modules.map((module) => (
              <div
                key={module.id}
                className="attachment-display-item"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                {module.type === 'protocol' && (
                  <ProtocolDisplay
                    protocol={module.data}
                    onRemove={null}
                    onBiohackClick={onBiohackClick}
                    userVotes={effectiveUserVotes}
                    handleVoteAction={effectiveHandleVote}
                    onAddProtocolToStaged={onAddProtocolToStaged}
                    mode={mode}
                  />
                )}
                {module.type === 'biomarker' && (
                  <BiomarkerDisplay
                    biomarker={module.data}
                    onRemove={null}
                  />
                )}
                {module.type === 'image' && (
                  <ImageDisplay
                    image={module.data}
                    onRemove={null}
                  />
                )}
                {module.type === 'biohacks' && (
                  <BiohacksDisplay
                    biohacks={module.data}
                    onBiohackClick={onBiohackClick}
                    onRemove={null}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  return renderContent();
};

export default AttachmentDisplay;

